import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import type { Transaction, TransactionType } from '../../types/financial';

interface TransactionFormProps {
  type: TransactionType;
  initialData?: Transaction;
  onSubmit: (data: Transaction) => void;
  onCancel: () => void;
}

const TransactionForm: React.FC<TransactionFormProps> = ({
  type,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [recurrence, setRecurrence] = useState(initialData?.recurrence || 'none');
  const [installments, setInstallments] = useState(1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const data: Transaction = {
      id: initialData?.id || crypto.randomUUID(),
      type,
      description: formData.get('description') as string,
      amount: parseFloat(formData.get('amount') as string),
      dueDate: new Date(formData.get('dueDate') as string),
      status: 'pending',
      category: formData.get('category') as string,
      recurrence: recurrence,
      notes: formData.get('notes') as string,
      createdAt: initialData?.createdAt || new Date(),
      updatedAt: new Date()
    };

    onSubmit(data);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex justify-between items-center p-6 border-b">
        <h2 className="text-xl font-semibold">
          {type === 'income' ? 'Novo Recebimento' : 'Nova Despesa'}
        </h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <input
              type="text"
              name="description"
              defaultValue={initialData?.description}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor
            </label>
            <input
              type="number"
              name="amount"
              defaultValue={initialData?.amount}
              required
              step="0.01"
              min="0"
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Vencimento
            </label>
            <input
              type="date"
              name="dueDate"
              defaultValue={initialData?.dueDate?.toISOString().split('T')[0]}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <input
              type="text"
              name="category"
              defaultValue={initialData?.category}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recorrência
            </label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value as Transaction['recurrence'])}
              className="w-full border rounded-md p-2"
            >
              <option value="none">Não Recorrente</option>
              <option value="daily">Diária</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensal</option>
              <option value="yearly">Anual</option>
            </select>
          </div>

          {recurrence === 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parcelas
              </label>
              <input
                type="number"
                value={installments}
                onChange={(e) => setInstallments(parseInt(e.target.value))}
                min="1"
                className="w-full border rounded-md p-2"
              />
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anexar Comprovante
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Fazer upload de arquivo</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                  </label>
                  <p className="pl-1">ou arraste e solte</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, PDF até 10MB
                </p>
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="notes"
              defaultValue={initialData?.notes}
              rows={3}
              className="w-full border rounded-md p-2"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {initialData ? 'Salvar Alterações' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionForm;